import { useState } from "react";
import {
    Box, Checkbox, Dialog, DialogActions, DialogContent, FormControlLabel,
    Radio, RadioGroup, Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useSnackbar } from "notistack";
import { apiConstants, postGenericBlob } from "../../common/constants";
import { MUTED, gradientBtnSx } from "./SectionCard";
import { DIALOG_PAPER_SX, TicketsDialogTitle } from "./TicketsUi";
import { COLORS } from "./emsTheme";

// Mirrors EscalationExportField on the backend — keep the enum names in sync.
const EXPORT_FIELDS = [
    { name: "TICKET_NUMBER", label: "Ticket #", defaultChecked: true },
    { name: "STATUS", label: "Status", defaultChecked: true },
    { name: "MERCHANT", label: "Merchant", defaultChecked: true },
    { name: "CATEGORY", label: "Category", defaultChecked: true },
    { name: "SUB_CATEGORY", label: "Sub Category", defaultChecked: false },
    { name: "COURIER", label: "Courier", defaultChecked: false },
    { name: "AWB_NUMBERS", label: "AWB", defaultChecked: false },
    { name: "SEVERITY", label: "Severity", defaultChecked: true },
    { name: "PLATFORM", label: "Platform (Proship / WMS / CT)", defaultChecked: true },
    { name: "VISIBLE_TO_MERCHANT", label: "Visible to Merchant", defaultChecked: false },
    { name: "CREATED_BY", label: "Created By", defaultChecked: false },
    { name: "CREATED_BY_EMAIL", label: "Created By Email", defaultChecked: false },
    { name: "ASSIGNED_TO", label: "Assigned To", defaultChecked: false },
    { name: "ASSIGNED_AT", label: "Assigned At", defaultChecked: false },
    { name: "REQUESTER", label: "Requester", defaultChecked: false },
    { name: "CREATED_AT", label: "Created", defaultChecked: true },
    { name: "UPDATED_AT", label: "Last Updated", defaultChecked: false },
    { name: "FIRST_RESPONSE_DUE_AT", label: "First Response Due", defaultChecked: false },
    { name: "RESOLUTION_DUE_AT", label: "Resolution Due By", defaultChecked: false },
    { name: "RESOLVED_AT", label: "Resolved At", defaultChecked: false },
    { name: "CLOSED_AT", label: "Closed At", defaultChecked: false },
    { name: "TAGS", label: "Tags", defaultChecked: false },
    { name: "DESCRIPTION", label: "Description", defaultChecked: false },
    { name: "TICKET_STAGE", label: "Ticket Stage (Open/Pending/Closed)", defaultChecked: false },
    { name: "SLA_STATUS", label: "SLA Status (Breached)", defaultChecked: false },
    { name: "SHIPMENT_STATUS", label: "Shipment Status", defaultChecked: false },
    { name: "ACKNOWLEDGED", label: "Acknowledged", defaultChecked: false },
    { name: "ACKNOWLEDGED_AT", label: "Acknowledged At", defaultChecked: false },
    { name: "LATEST_COMMENT", label: "Latest Comment (and its timestamp)", defaultChecked: false },
    { name: "COMMENTS", label: "Last 3 Comments (each with its timestamp)", defaultChecked: false },
    { name: "STATUS_MOVEMENT", label: "Status movement (each status with its date & time)", defaultChecked: false },
];

const ExportTicketsModal = ({ open, onClose, filters }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [format, setFormat] = useState("CSV");
    const [selectedFields, setSelectedFields] = useState(
        EXPORT_FIELDS.filter((f) => f.defaultChecked).map((f) => f.name)
    );
    const [includeAttachments, setIncludeAttachments] = useState(false);
    const [downloading, setDownloading] = useState(false);

    const toggleField = (name) => {
        setSelectedFields((prev) => prev.includes(name) ? prev.filter((f) => f !== name) : [...prev, name]);
    };
    const allSelected = selectedFields.length === EXPORT_FIELDS.length;
    // Every column, in the list's order, or none.
    const toggleAll = () => setSelectedFields(allSelected ? [] : EXPORT_FIELDS.map((f) => f.name));

    const handleExport = async () => {
        if (selectedFields.length === 0) {
            enqueueSnackbar("Pick at least one field to export.", { variant: "warning" });
            return;
        }
        setDownloading(true);
        try {
            const response = await postGenericBlob(apiConstants.ESCALATION_EXPORT_V2, {
                filters,
                format,
                fields: includeAttachments ? [...selectedFields, "ATTACHMENTS"] : selectedFields,
            });
            const disposition = response.headers?.['content-disposition'] || '';
            const match = disposition.match(/filename=([^;]+)/);
            const fileName = match ? match[1].trim() : `escalations.${format === 'EXCEL' ? 'xlsx' : 'csv'}`;

            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            link.remove();
            // Free the file's memory once the browser has started the download.
            setTimeout(() => window.URL.revokeObjectURL(url), 0);
            onClose();
        } catch (err) {
            enqueueSnackbar(await extractErrorMessage(err), { variant: "error" });
        } finally {
            setDownloading(false);
        }
    };

    // responseType:'blob' means an error response body arrives as a Blob too, not parsed JSON —
    // err.response.data.message is always undefined without this, silently hiding the backend's
    // actual reason (e.g. "over the 20000 ticket limit") behind a generic fallback.
    const extractErrorMessage = async (err) => {
        const data = err?.response?.data;
        if (data instanceof Blob) {
            try {
                const text = await data.text();
                const parsed = JSON.parse(text);
                if (parsed?.message) return parsed.message;
            } catch (e) {
                // Not JSON (or empty) — fall through to the generic message below.
            }
        } else if (data?.message) {
            return data.message;
        }
        return "Could not export tickets.";
    };

    return (
        <Dialog open={open} onClose={() => !downloading && onClose()} maxWidth="xs" fullWidth PaperProps={{ sx: DIALOG_PAPER_SX }}>
            <TicketsDialogTitle icon={FileDownloadIcon} title="Export Tickets" sub="Download the tickets matching your filters" onClose={onClose} closeDisabled={downloading} />
            <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1, "& .MuiCheckbox-root.Mui-checked, & .MuiRadio-root.Mui-checked": { color: COLORS.purple } }}>
                <Typography sx={{ fontSize: 13, color: MUTED }}>
                    Exports every ticket matching the list's current tab and filters — not just the tickets on this page.
                </Typography>

                <Box>
                    <Typography sx={{ fontSize: 12, color: MUTED, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em", mb: 0.5 }}>
                        Format
                    </Typography>
                    <RadioGroup row value={format} onChange={(e) => setFormat(e.target.value)}>
                        <FormControlLabel value="CSV" control={<Radio size="small" />} label="CSV" />
                        <FormControlLabel value="EXCEL" control={<Radio size="small" />} label="Excel" />
                    </RadioGroup>
                </Box>

                <Box>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                        <Typography sx={{ fontSize: 12, color: MUTED, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em" }}>
                            Columns ({selectedFields.length} selected)
                        </Typography>
                        <FormControlLabel
                            sx={{ mr: 0 }}
                            control={
                                <Checkbox
                                    size="small"
                                    checked={allSelected}
                                    indeterminate={selectedFields.length > 0 && !allSelected}
                                    onChange={toggleAll}
                                />
                            }
                            label={<Typography sx={{ fontSize: 13, fontWeight: 600 }}>Select all</Typography>}
                        />
                    </Box>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", maxHeight: 260, overflowY: "auto" }}>
                        {EXPORT_FIELDS.map((f) => (
                            <FormControlLabel
                                key={f.name}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={selectedFields.includes(f.name)}
                                        onChange={() => toggleField(f.name)}
                                    />
                                }
                                label={<Typography sx={{ fontSize: 13 }}>{f.label}</Typography>}
                            />
                        ))}
                    </Box>
                </Box>

                <Box sx={{ borderTop: `1px solid ${COLORS.grid}`, pt: 1.5 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={includeAttachments}
                                onChange={(e) => setIncludeAttachments(e.target.checked)}
                            />
                        }
                        label={<Typography sx={{ fontSize: 13, fontWeight: 600 }}>Include attachment links</Typography>}
                    />
                    <Typography sx={{ fontSize: 12, color: MUTED, ml: 4 }}>
                        Adds one column per attachment with a link to download it. Each export makes fresh links that work for 7 days from then; export again any time for new ones.
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <LoadingButton
                    variant="contained"
                    loading={downloading}
                    onClick={handleExport}
                    sx={gradientBtnSx}
                >
                    Export
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default ExportTicketsModal;
